/**
 * Desktop — a player's page (DESIGN-CHANGELOG 2026-09-03 "A player's page";
 * `design/GT Crew.dc.html:371-433`). AA phase 5 §J.
 *
 * Full-screen behind the PARTY tab, read-only. `◂ BACK TO PARTY` · avatar ·
 * name · role chip; a boxed tab strip (RATINGS, then GOTY <year> per year
 * they have a list for); WHAT THEY PLAYED as 2:3 covers with the 0–10 badge
 * on the art, high to low; THEIR GOTY as tier rows of 68×102 cover chips.
 * Every tile opens the game. Nothing here writes.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { memberVM } from '../../store/selectors.ts';
import type { ArtCand, GameId } from '../../lib/types.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function ScoreTile({ id, n, scTxt, scClr, ini, fall, coverCands }: { id: GameId; n: string; scTxt: string; scClr: string; ini: string; fall: string; coverCands: ArtCand[] }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(id + 'p', coverCands);
  return (
    <div onClick={() => openGame(id)} style={{ position: 'relative', aspectRatio: '2/3', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={ini} hasCover={art.hasCover} cover={art.cover} />
      <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: 'var(--gt-ink)', borderLeft: '1px solid ' + scClr, borderBottom: '1px solid ' + scClr, padding: '6px 8px', zIndex: 2 }}>
        <span style={{ fontFamily: PIXEL, fontSize: 12, color: scClr, transform: 'translateY(0.13em)' }}>{scTxt}</span>
        <span style={{ fontFamily: MONO, fontSize: 6, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>SCORE</span>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '18px 10px 9px', background: 'linear-gradient(180deg,transparent,rgba(5,5,8,.94))', fontWeight: 600, fontSize: 12.5, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {n}
      </div>
    </div>
  );
}

function Chip({ id, n, ini, fall, coverCands }: { id: GameId; n: string; ini: string; fall: string; coverCands: ArtCand[] }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(id + 'p', coverCands);
  return (
    <div onClick={() => openGame(id)} data-tip={n} className="d-hov-acc-bd" style={{ position: 'relative', width: 68, height: 102, flex: 'none', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={9} ini={ini} hasCover={art.hasCover} cover={art.cover} />
    </div>
  );
}

export default function Member(): JSX.Element {
  const s = useQd();
  const v = memberVM(s);

  // Same art fetch the phone page runs — see the note there.
  useQueueArt([...v.scoreRows.map((r) => r.id), ...v.memTiers.flatMap((t) => t.chips.map((c) => c.id))]);

  return (
    <div data-screen-label="Player page" style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '22px 26px 40px', maxWidth: 1180, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div onClick={() => s.nav('crew')} className="d-hov-acc" style={{ display: 'flex', alignItems: 'center', height: 40, padding: '0 12px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', cursor: 'pointer', boxSizing: 'border-box' }}>
          ◂ BACK TO PARTY
        </div>
        <div style={{ position: 'relative', width: 40, height: 40, background: v.hueBg, display: 'grid', placeItems: 'center', fontFamily: PIXEL, fontSize: 12, color: 'var(--gt-ink)', overflow: 'hidden' }}>
          {v.ini}
          {v.hasAv ? <div style={{ position: 'absolute', inset: 0, background: v.avBg }} /> : null}
        </div>
        <span style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>{v.nameUp}</span>
        <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.08em', color: v.roleClr, border: '1px solid ' + v.roleClr, padding: '3px 6px' }}>{v.roleLbl}</span>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-faint)' }}>{v.statTxt}</span>
      </div>
      <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10 }}>
        {v.tabs.map((t) => (
          <div key={t.key} onClick={() => s.setMemTab(t.key)} style={{ height: 40, display: 'flex', alignItems: 'center', padding: '0 16px', border: '1px solid ' + t.bd, color: t.clr, background: t.bg, marginLeft: -1, position: 'relative', zIndex: t.on ? 1 : 0, cursor: 'pointer', boxSizing: 'border-box' }}>
            {t.l}
          </div>
        ))}
      </div>
      {v.showScores ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>WHAT THEY PLAYED</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 178px))', gap: 14 }}>
            {v.scoreRows.map((r) => (
              <ScoreTile key={r.key} id={r.id} n={r.n} scTxt={r.scTxt} scClr={r.scClr} ini={r.ini} fall={r.fall} coverCands={r.coverCands} />
            ))}
          </div>
          {v.scoresEmpty ? <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)', padding: '20px 0' }}>{v.scoresEmptyTxt}</div> : null}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--gt-border)', background: 'var(--gt-card)', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>THEIR GOTY</span>
          {v.memTiers.map((row) => (
            <div key={row.key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ width: 26, flex: 'none', fontFamily: PIXEL, fontSize: 13, color: row.clr, transform: 'translateY(0.13em)', paddingTop: 4 }}>{row.t}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {row.chips.map((c) => (
                  <Chip key={c.key} id={c.id} n={c.n} ini={c.ini} fall={c.fall} coverCands={c.coverCands} />
                ))}
              </div>
            </div>
          ))}
          {v.gotyEmpty ? <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)', padding: '10px 0' }}>{v.gotyEmptyTxt}</div> : null}
        </div>
      )}
    </div>
  );
}
